import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EXPLORE_UNKNOWN_MINIGAME_SURFACES } from './config/exploreUnknownMinigames';
import { getExploreUnknownMinigameEntry } from './exploreUnknownMinigames/exploreUnknownMinigameRegistry';

/**
 * Dev-only mobile panel: launch any Explore Unknown minigame without visiting each map surface.
 * Uses POST /explore-unknown/dev-force-minigame with the surface that owns each minigame.
 */
export default function ExploreUnknownMobileMinigameDevLauncher({
  busy = false,
  error = null,
  onLaunchSurface,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    return Object.entries(EXPLORE_UNKNOWN_MINIGAME_SURFACES)
      .filter(([, entry]) => entry.enabled === true && typeof entry.minigameId === 'string' && entry.minigameId)
      .map(([surface, entry]) => {
        const registryEntry = getExploreUnknownMinigameEntry(entry.minigameId);
        return {
          surface,
          minigameId: entry.minigameId,
          label: t(registryEntry.titleI18nKey),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [t]);

  if (options.length === 0) {
    throw new Error('ExploreUnknownMobileMinigameDevLauncher: no enabled minigames in config');
  }

  return (
    <div className="explore-unknown-mobile-minigame-dev" role="region" aria-label="Dev: launch minigames">
      <button
        type="button"
        className="explore-unknown-mobile-minigame-dev-toggle"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide games' : 'Dev games'}
      </button>
      {open ? (
        <div className="explore-unknown-mobile-minigame-dev-panel">
          <div className="explore-unknown-mobile-minigame-dev-grid">
            {options.map(({ surface, label }) => (
              <button
                key={surface}
                type="button"
                className="explore-unknown-mobile-minigame-dev-btn"
                disabled={busy || disabled}
                onClick={() => {
                  onLaunchSurface(surface);
                }}
              >
                {busy ? '…' : label}
              </button>
            ))}
          </div>
          {error ? (
            <p className="explore-unknown-mobile-minigame-dev-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
